import React, { useState, useRef, useEffect } from "react";
import { StarIcon } from "@heroicons/react/24/solid";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

const REVIEWS = [
  {
    id: 1,
    name: "Sarah Jenkins",
    location: "United Kingdom",
    rating: 5,
    text: "Saved me so much money on my trip to Japan! The setup was instant and the speed was consistently 5G. Highly recommend over traditional roaming.",
    date: "2 days ago",
  },
  {
    id: 2,
    name: "Michael Chen",
    location: "Singapore",
    rating: 5,
    text: "Used this for a multi-city Euro trip. Seamlessly switched networks as I crossed borders from France to Germany. Super convenient app experience.",
    date: "1 week ago",
  },
  {
    id: 3,
    name: "Jessica Alverez",
    location: "USA",
    rating: 5,
    text: "I was skeptical about eSIMs but this was effortless. Scanned the QR code and I had data before I even left the airport. Will definitely use again.",
    date: "3 weeks ago",
  },
  {
    id: 4,
    name: "David Smith",
    location: "Australia",
    rating: 4,
    text: "Great coverage and valuable plans. The only reason for 4 stars is I wish there were more unlimited data options, but for the price, it's unbeatable.",
    date: "1 month ago",
  },
  {
    id: 5,
    name: "Emma Wilson",
    location: "Canada",
    rating: 5,
    text: "Customer service was helpful when I had a question about activation. The service itself worked flawlessly during my 2 weeks in Thailand.",
    date: "1 month ago",
  },
];

export default function ReviewsCarousel() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener("resize", checkScroll);
    return () => window.removeEventListener("resize", checkScroll);
  }, []);

  const scroll = (direction: "left" | "right") => {
    if (scrollRef.current) {
      const { clientWidth } = scrollRef.current;
      const scrollAmount = direction === "left" ? -clientWidth / 1.2 : clientWidth / 1.2;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
      setTimeout(checkScroll, 300); // Check after scroll animation roughly finishes
    }
  };

  return (
    <section className="py-16 bg-gray-50 rounded-2xl my-8">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row justify-between items-center mb-10">
          <div className="text-left mb-6 md:mb-0">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Trusted by Travelers Worldwide</h2>
            <p className="text-gray-600">See what our customers are saying about their connectivity experience.</p>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={() => scroll("left")}
              disabled={!canScrollLeft}
              className={`p-2 rounded-full border transition-colors ${
                !canScrollLeft
                  ? "border-gray-200 text-gray-300 cursor-not-allowed"
                  : "border-gray-300 text-gray-700 hover:bg-white hover:shadow-md hover:border-[#090EDB]"
              }`}
            >
              <ChevronLeftIcon className="w-6 h-6" />
            </button>
            <button
              onClick={() => scroll("right")}
              disabled={!canScrollRight}
              className={`p-2 rounded-full border transition-colors ${
                !canScrollRight
                  ? "border-gray-200 text-gray-300 cursor-not-allowed"
                  : "border-gray-300 text-gray-700 hover:bg-white hover:shadow-md hover:border-[#090EDB]"
              }`}
            >
              <ChevronRightIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div
          ref={scrollRef}
          onScroll={checkScroll}
          className="flex gap-6 overflow-x-auto snap-x snap-mandatory pb-8 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0 [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {REVIEWS.map((review) => (
            <div
              key={review.id}
              className="min-w-[85%] md:min-w-[400px] bg-white p-6 rounded-xl shadow-sm border border-gray-100 snap-start flex flex-col hover:shadow-md transition-shadow"
            >
              <div className="flex items-center gap-1 mb-4 text-[#FFB02E]">
                 {[...Array(5)].map((_, i) => (
                    <StarIcon key={i} className={`w-5 h-5 ${i < review.rating ? "text-yellow-400" : "text-gray-200"}`} />
                 ))}
              </div>
              
              <p className="text-gray-700 mb-6 flex-grow leading-relaxed">&quot;{review.text}&quot;</p>
              
              <div className="flex justify-between items-end border-t pt-4 border-gray-50">
                <div>
                    <p className="font-bold text-gray-900">{review.name}</p>
                    <p className="text-sm text-gray-500">{review.location}</p>
                </div>
                <span className="text-xs text-gray-400">{review.date}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
